/**
 * Permission Utilities
 * Client-side utilities for evaluating permissions and scopes
 * Matches backend ScopeEvaluationService logic
 */

import type { RoleScope, ScopePattern, PermissionContext } from '@/types/roles'

/**
 * Parse a scope string into its components
 * Format: "resource:operation" or "resource:operation:path"
 * 
 * @param scope - Scope string to parse
 * @returns Parsed scope pattern
 */
export function parseScope(scope: RoleScope): ScopePattern {
  const parts = scope.split(':')
  
  return {
    resource: parts[0] || '*',
    operation: parts[1] || '*',
    path: parts.length > 2 ? parts.slice(2).join(':') : undefined
  }
}

/**
 * Check if a path matches a scope pattern
 * 
 * Supports multiple pattern types:
 * - Exact match: "database" matches "database"
 * - Parent match: "database" matches "database.timeout" (grants access to children)
 * - Deep wildcard: "database.**" matches all descendants
 * - Child wildcard: "database.*" matches direct children only
 * - Glob anywhere: "*.Licensing" matches "Licensing" at any nesting level
 * - Nested path: "configuration.database" matches nested path and children
 * 
 * @param pattern - The pattern to check (e.g., "*", "database", "database.*", "*.Licensing")
 * @param value - The value to match against (e.g., "database.timeout")
 * @returns True if the pattern matches the value
 */
function matchesPattern(pattern: string, value: string): boolean {
  // Exact wildcard match - grants all access
  if (pattern === '*') {
    return true
  }
  
  // Exact match
  if (pattern === value) {
    return true
  }
  
  // Deep wildcard: "database.**" matches database.x.y.z
  if (pattern.endsWith('.**')) {
    const prefix = pattern.slice(0, -3)
    // Match the prefix itself OR anything that starts with prefix.
    return value === prefix || value.startsWith(prefix + '.')
  }
  
  // Child wildcard: "database.*" matches database.timeout but not database.connection.timeout
  if (pattern.endsWith('.*')) {
    const prefix = pattern.slice(0, -2)
    
    // Must start with the prefix
    if (!value.startsWith(prefix + '.')) {
      return false
    }
    
    // Get the part after the prefix
    const remainder = value.slice(prefix.length + 1)
    
    // Should not contain another dot (i.e., should be direct child)
    return !remainder.includes('.')
  }
  
  // Glob anywhere: "*.Licensing" matches Licensing at any level
  if (pattern.startsWith('*.')) {
    const suffix = pattern.slice(2)
    
    // Match if value ends with the suffix as a complete segment
    // E.g., "*.Licensing" matches "Licensing", "config.Licensing", "tenant.settings.Licensing"
    // but not "LicensingInfo"
    if (value === suffix) {
      return true
    }
    
    // Check if suffix appears as a complete path segment
    const segments = value.split('.')
    return segments.includes(suffix)
  }
  
  // Parent path match: "database" matches "database.timeout"
  // This grants access to the property and all its children
  if (value.startsWith(pattern + '.')) {
    return true
  }
  
  return false
}

/**
 * Check if a specific scope grants permission for a resource, operation, and optional path
 * 
 * @param scope - The scope to evaluate (e.g., "presets:write:database.*")
 * @param resource - The resource being accessed (e.g., "presets")
 * @param operation - The operation being performed (e.g., "write")
 * @param path - Optional path within the resource (e.g., "database.connectionString")
 * @returns True if the scope grants permission
 */
export function matchesScope(
  scope: RoleScope,
  resource: string,
  operation: string,
  path?: string
): boolean {
  const pattern = parseScope(scope)
  
  // Check resource match
  if (!matchesPattern(pattern.resource, resource)) {
    return false
  }
  
  // Check operation match
  if (!matchesPattern(pattern.operation, operation)) {
    return false
  }
  
  // If no path is specified in the scope, it grants access to the entire resource
  if (!pattern.path) {
    return true
  }
  
  // If a path is specified in the scope but no path is being checked, deny access
  // (path-specific scopes don't grant blanket access)
  if (!path) {
    return false
  }
  
  // Check path match
  return matchesPattern(pattern.path, path)
}

/**
 * Check if any of the provided scopes grant permission for a resource, operation, and optional path
 * 
 * @param scopes - Array of scopes to evaluate
 * @param resource - The resource being accessed
 * @param operation - The operation being performed
 * @param path - Optional path within the resource
 * @returns True if any scope grants permission
 */
export function canPerformOperation(
  scopes: RoleScope[],
  resource: string,
  operation: string,
  path?: string
): boolean {
  return scopes.some(scope => matchesScope(scope, resource, operation, path))
}

/**
 * Check if the current user can access a specific path within a resource
 * This is a convenience function for checking write access to configuration paths
 * 
 * @param scopes - Array of user scopes
 * @param resource - The resource being accessed (e.g., "presets", "tenants")
 * @param path - The configuration path (e.g., "database.connectionString", "Licensing.maxUsers")
 * @returns True if the user can write to the path
 */
export function canAccessPath(
  scopes: RoleScope[],
  resource: string,
  path: string
): boolean {
  return canPerformOperation(scopes, resource, 'write', path)
}

/**
 * Combine multiple arrays of scopes into a single deduplicated array
 * 
 * @param scopeArrays - Multiple arrays of scopes to combine
 * @returns Combined and deduplicated array of scopes
 */
export function combineScopes(...scopeArrays: RoleScope[][]): RoleScope[] {
  const combined = new Set<RoleScope>()
  
  for (const scopes of scopeArrays) {
    for (const scope of scopes) {
      combined.add(scope)
    }
  }
  
  return Array.from(combined)
}

/**
 * Get all resources that can be accessed with a specific operation
 * 
 * @param scopes - Array of user scopes
 * @param operation - The operation to check (e.g., "read", "write")
 * @returns Array of accessible resources
 */
export function getAccessibleResources(
  scopes: RoleScope[],
  operation: string
): string[] {
  const resources = new Set<string>()
  
  for (const scope of scopes) {
    const pattern = parseScope(scope)
    
    // Check if this scope grants the requested operation
    if (matchesPattern(pattern.operation, operation) || pattern.operation === '*') {
      // Add the resource (or all resources if wildcard)
      if (pattern.resource === '*') {
        resources.add('presets')
        resources.add('tenants')
        resources.add('schemas')
        resources.add('roles')
        resources.add('users')
        resources.add('subscribers')
      } else {
        resources.add(pattern.resource)
      }
    }
  }
  
  return Array.from(resources)
}

/**
 * Get all operations that can be performed on a specific resource
 * 
 * @param scopes - Array of user scopes
 * @param resource - The resource to check
 * @param path - Optional path within the resource
 * @returns Array of allowed operations
 */
export function getAllowedOperations(
  scopes: RoleScope[],
  resource: string,
  path?: string
): string[] {
  const operations = new Set<string>()
  const commonOps = ['read', 'write', 'create', 'delete']
  
  for (const scope of scopes) {
    const pattern = parseScope(scope)
    
    // Check if this scope applies to the resource
    if (!matchesPattern(pattern.resource, resource)) {
      continue
    }
    
    // If path is specified, check path match
    if (path && pattern.path && !matchesPattern(pattern.path, path)) {
      continue
    }
    
    // If path is specified in scope but we're checking without path, skip
    if (!path && pattern.path) {
      continue
    }
    
    // Add the operation (or all operations if wildcard)
    if (pattern.operation === '*') {
      commonOps.forEach(op => operations.add(op))
    } else {
      operations.add(pattern.operation)
    }
  }
  
  return Array.from(operations)
}

/**
 * Check if user has read-only access (can read but not write)
 * 
 * @param scopes - Array of user scopes
 * @param resource - The resource to check
 * @returns True if user can only read the resource
 */
export function isReadOnly(scopes: RoleScope[], resource: string): boolean {
  const canRead = canPerformOperation(scopes, resource, 'read')
  const canWrite = canPerformOperation(scopes, resource, 'write')
  
  return canRead && !canWrite
}

/**
 * Check if user has full access to a resource (all operations)
 * 
 * @param scopes - Array of user scopes
 * @param resource - The resource to check
 * @returns True if user has full access
 */
export function hasFullAccess(scopes: RoleScope[], resource: string): boolean {
  return canPerformOperation(scopes, resource, 'read') &&
         canPerformOperation(scopes, resource, 'write') &&
         canPerformOperation(scopes, resource, 'create') &&
         canPerformOperation(scopes, resource, 'delete')
}

/**
 * Evaluate permission context to determine if access should be granted
 * 
 * @param context - Permission evaluation context
 * @returns True if access should be granted
 */
export function evaluatePermission(context: PermissionContext): boolean {
  return canPerformOperation(
    context.userScopes,
    context.resource,
    context.operation,
    context.path
  )
}
